import { Calendar } from 'lucide-react';
import ChipTip from '@/components/ChipTip';

/**
 * ExDividendThisWeekLine — standalone strip listing watchlist tickers
 * whose NEXT ex-dividend date falls within the next 7 days (inclusive
 * of today). Lives outside the WatchlistSummaryCard so the strip is
 * never gated by that card's internal render logic. Self-hides when
 * nothing qualifies.
 *
 * Round 95bc/95be (Jun 2026). Mirrors the inline-chip pattern of
 * UnusualActivityLine and RecentDividendEventsLine.
 *
 * Data note: backend stores `ex_dividend_date` as the LAST (past)
 * ex-div, with `next_ex_dividend_date` populated when known. Weekly
 * dividend tickers (e.g., QQQY, TSYY, ULTI) routinely have a NULL
 * `next_ex_dividend_date` because Massive doesn't pre-announce —
 * but a weekly's next ex-div is mechanically 7 days after the last
 * one. We derive a forward-looking date when the explicit one is
 * missing, using the ticker's `frequency` cadence.
 */
const ONE_DAY_MS = 86_400_000;

// Mapping from canonical frequency labels to typical inter-ex-div
// spacing in days. Bi-Weekly is 14; "Semi-Annually" is half a
// year ≈ 182. These are nominal — the next ex-div can drift a
// day or two — but for a ≤ 7-day "this week" filter that drift
// only matters at the boundary (and is the right behavior: a
// Monthly that's slightly past its 30-day mark genuinely is "due
// this week").
const FREQ_DAYS = {
  Daily: 1,
  Weekly: 7,
  'Bi-Weekly': 14,
  Biweekly: 14,
  Monthly: 30,
  Quarterly: 91,
  'Semi-Annually': 182,
  Annually: 365,
};

// Round P145 (Feb 2026) — Mon-Fri labels for the daily-payer range
// chip. SATA (first daily payer) needs to render as ONE chip that
// spans the week ("Mon-Fri", "Aug 24-28") instead of 5 separate
// per-day chips that would blow out the strip on a phone.
const _WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// NYSE holidays this week are unusual — a lightweight client-side
// check works. Format: YYYY-MM-DD strings observed as NYSE closed
// through 2028. Update annually.
const _NYSE_HOLIDAYS_2026_2028 = new Set([
  '2026-01-01', '2026-01-19', '2026-02-16', '2026-04-03', '2026-05-25',
  '2026-06-19', '2026-07-03', '2026-09-07', '2026-11-26', '2026-12-25',
  '2027-01-01', '2027-01-18', '2027-02-15', '2027-03-26', '2027-05-31',
  '2027-06-18', '2027-07-05', '2027-09-06', '2027-11-25', '2027-12-24',
  '2028-01-17', '2028-02-21', '2028-04-14', '2028-05-29', '2028-06-19',
  '2028-07-04', '2028-09-04', '2028-11-23', '2028-12-25',
]);

function _isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function _isBusinessDay(d) {
  const wd = d.getDay();
  if (wd === 0 || wd === 6) return false;
  return !_NYSE_HOLIDAYS_2026_2028.has(_isoDate(d));
}

/**
 * For a daily payer, return the list of business days remaining in
 * the current calendar week (from today through this coming Friday).
 * Returns null when no business days remain (e.g., called on a
 * weekend). The Ex-Div This Week line renders these as a single
 * range chip like "Aug 24 – 28".
 */
function _daysThisWeekFor(today) {
  const businessDays = [];
  // Today's day-of-week; 0 = Sunday, 5 = Friday, 6 = Saturday
  const startDow = today.getDay();
  const daysUntilSaturday = 6 - startDow; // 0 if today is Saturday
  for (let i = 0; i <= daysUntilSaturday; i += 1) {
    const d = new Date(today.getTime() + i * ONE_DAY_MS);
    if (_isBusinessDay(d)) businessDays.push(d);
  }
  return businessDays.length > 0 ? businessDays : null;
}

function _formatDayRange(days) {
  if (days.length === 1) {
    const d = days[0];
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  const first = days[0];
  const last = days[days.length - 1];
  const monthShort = first.toLocaleDateString('en-US', { month: 'short' });
  // Same month → "Aug 24–28", crossing months → "Aug 30 – Sep 3"
  if (first.getMonth() === last.getMonth()) {
    return `${monthShort} ${first.getDate()}–${last.getDate()}`;
  }
  const lastMonthShort = last.toLocaleDateString('en-US', { month: 'short' });
  return `${monthShort} ${first.getDate()} – ${lastMonthShort} ${last.getDate()}`;
}

function parseDate(raw) {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function deriveNextExDiv(stock, today) {
  // Prefer the explicit forward-looking field when the backend supplies it.
  // Oct 2026 — the list payload carries `next_ex_date` (the same value the
  // Stock Modal's "Next Payment" card shows); only the SEO endpoints rename
  // it to `next_ex_dividend_date`. Honour both before stepping a cadence,
  // otherwise a Monthly payer shows "4d" (last ex + 30) while the modal
  // says Nov 2 (6d).
  const explicit = parseDate(stock.next_ex_dividend_date) || parseDate(stock.next_ex_date);
  if (explicit && explicit.getTime() >= today.getTime()) return explicit;

  // Otherwise, step forward from the last ex-div by the cadence implied
  // by `frequency`. We keep stepping until the candidate lands at-or-
  // after today — this handles backends where `ex_dividend_date` is
  // multiple cadences old (e.g., TSYY's last_ex stored as Jun 05 for a
  // Weekly ticker means the next ex-div is actually Jun 19, not Jun 12).
  // Capped at 60 iterations as a safety net so a mis-coded frequency
  // never causes an infinite loop.
  const last = parseDate(stock.ex_dividend_date);
  const step = FREQ_DAYS[stock.frequency];
  if (!last || !step) return null;
  let candidate = new Date(last.getTime() + step * ONE_DAY_MS);
  let guard = 0;
  while (candidate.getTime() < today.getTime() && guard < 60) {
    candidate = new Date(candidate.getTime() + step * ONE_DAY_MS);
    guard += 1;
  }
  return candidate;
}

export default function ExDividendThisWeekLine({ stocks = [], onStockClick }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Round P145 (Feb 2026) — Daily payers get one range chip. SATA
  // (first daily payer) is ex-div every business day this week, so
  // collapse to a single "Aug 24–28" chip instead of five copies of
  // the same ticker cluttering the strip. Runs before the standard
  // "next-ex within 7 days" filter so daily rows never show up twice.
  const dailyBusinessDays = _daysThisWeekFor(today);
  const dailyEntries = (stocks || [])
    .filter((s) => s?.frequency === 'Daily' && dailyBusinessDays)
    .map((s) => ({
      ticker: s.ticker,
      isDaily: true,
      rangeLabel: _formatDayRange(dailyBusinessDays),
      dayCount: dailyBusinessDays.length,
      amount: s.last_dividend_value,
    }));

  const dailyTickerSet = new Set(dailyEntries.map((e) => e.ticker));

  const exDivSoon = (stocks || [])
    .filter((s) => !dailyTickerSet.has(s?.ticker))
    .map((s) => {
      const next = deriveNextExDiv(s || {}, today);
      if (!next) return null;
      const days = Math.round((next.getTime() - today.getTime()) / ONE_DAY_MS);
      if (days < 0 || days > 7) return null;
      return {
        ticker: s.ticker,
        days,
        amount: s.last_dividend_value,
        derived: !s.next_ex_dividend_date,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.days - b.days);

  const totalCount = dailyEntries.length + exDivSoon.length;
  if (totalCount === 0) return null;

  return (
    <div
      className="my-3 px-3 py-2 rounded-lg border bg-card flex flex-wrap items-center gap-2"
      data-testid="watchlist-exdiv-this-week-line"
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
        <Calendar className="w-3.5 h-3.5 text-amber-500" />
        Ex-Div This Week ({totalCount})
      </span>
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Daily payers first — one range chip per ticker */}
        {dailyEntries.map((e) => {
          const title = e.amount != null
            ? `${e.ticker} is a daily payer — ex-div every business day (${e.rangeLabel}, ${e.dayCount} days). $${Number(e.amount).toFixed(4)} per business day.`
            : `${e.ticker} is a daily payer — ex-div every business day (${e.rangeLabel}, ${e.dayCount} days).`;
          return (
            <span key={`daily-${e.ticker}`} className="inline-flex items-center gap-1 px-1 py-[2px] rounded" data-testid={`exdiv-daily-${e.ticker}`}>
              <button
                type="button"
                onClick={() => onStockClick && onStockClick(e.ticker)}
                className="text-[11px] font-bold font-mono px-0.5 rounded hover:bg-muted transition-colors"
                title={`Open ${e.ticker} in the Stock Modal`}
              >
                {e.ticker}
              </button>
              <ChipTip
                text={title}
                ticker={e.ticker}
                onOpenTicker={onStockClick}
                testId={`exdiv-daily-pill-${e.ticker}`}
              >
                <span className="inline-flex items-center gap-1">
                  <span className="text-[9px] font-bold px-1 py-[1px] rounded-sm bg-cyan-100 dark:bg-cyan-950/40 text-cyan-700 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-700 whitespace-nowrap">
                    {e.rangeLabel}
                  </span>
                  {e.amount != null && (
                    <span className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400">
                      ${Number(e.amount).toFixed(2)}/d
                    </span>
                  )}
                </span>
              </ChipTip>
            </span>
          );
        })}
        {exDivSoon.map((e) => {
          const when =
            e.days === 0 ? 'today' : e.days === 1 ? '1d' : `${e.days}d`;
          const title = e.amount != null
            ? `${e.ticker} ex-div in ${when} · $${Number(e.amount).toFixed(4)} expected${e.derived ? ' (estimated from cadence)' : ''}`
            : `${e.ticker} ex-div in ${when}${e.derived ? ' (estimated from cadence)' : ''}`;
          return (
            <span key={e.ticker} className="inline-flex items-center gap-1 px-1 py-[2px] rounded">
              <button
                type="button"
                onClick={() => onStockClick && onStockClick(e.ticker)}
                className="text-[11px] font-bold font-mono px-0.5 rounded hover:bg-muted transition-colors"
                data-testid={`exdiv-chip-${e.ticker}`}
                title={`Open ${e.ticker} in the Stock Modal`}
              >
                {e.ticker}
              </button>
              <ChipTip
                text={title}
                ticker={e.ticker}
                onOpenTicker={onStockClick}
                testId={`exdiv-pill-${e.ticker}`}
              >
                <span className="inline-flex items-center gap-1">
                  <span className="text-[9px] font-bold px-1 py-[1px] rounded-sm bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700 whitespace-nowrap">
                    {when}
                    {e.derived && <span className="opacity-60">~</span>}
                  </span>
                  {e.amount != null && (
                    <span className="text-[9px] font-mono text-emerald-600 dark:text-emerald-400">
                      ${Number(e.amount).toFixed(2)}
                    </span>
                  )}
                </span>
              </ChipTip>
            </span>
          );
        })}
      </div>
    </div>
  );
}
